import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import {
    HiOutlineClock,
    HiOutlineUserCircle,
    HiOutlineTag,
    HiOutlinePencilAlt,
} from 'react-icons/hi'
import { Tooltip } from 'components/ui'
import AvatarProfile from 'components/ui/Avatar/AvatarProfile'
import { priorityStatusColor } from 'utils/storeHube/statusColor'
import { cleanIsoDate } from 'utils/storeHube/helpersDate'

// Separador entre items.
const Sep = () => (
    <span
        className="inline-block w-px h-3 bg-gray-300 dark:bg-gray-600 mx-1.5"
        aria-hidden="true"
    />
)

/* --------------------------------------------------------------------------
 * TicketMetaStrip — fila compacta de metadata de alta señal debajo del titulo.
 *
 * Solo lo mas usado:
 *   - Asignado (Operator) — clickeable para reasignar
 *   - Categoria padre
 *   - Vencimiento (con color por urgencia)
 *
 * El resto (Prioridad, Creado por, Creado el, External code, Sample
 * since/until, Origen) vive en el TicketDetailsCard del aside lateral.
 * -------------------------------------------------------------------------- */
const TicketMetaStrip = ({
    data,
    onChangeOperator,
    canUpdate,
    loadingUsersAllowed,
}) => {
    const { t } = useTranslation()
    const { expired_at, props } = data
    const operator = props?.operator
    const ticket_category_primary = props?.ticket_category_primary

    const expiresClean = cleanIsoDate(expired_at)
    const isExpired = expiresClean ? dayjs(expiresClean).isBefore(dayjs()) : false
    const expirationLabel = expiresClean
        ? dayjs(expiresClean).locale('es').format('DD MMM YYYY')
        : null

    const parentCategory = ticket_category_primary?.parent?.label?.es

    return (
        <div className="mb-5 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-y-2.5 text-[12.5px]">
                <Tooltip
                    title={
                        canUpdate
                            ? t('change_assignment', 'Cambiar asignación')
                            : operator?.email || ''
                    }
                >
                    <button
                        type="button"
                        data-clarity-event="assign-operator"
                        onClick={canUpdate ? onChangeOperator : undefined}
                        disabled={!canUpdate || loadingUsersAllowed}
                        className={[
                            'group inline-flex items-center gap-1.5 px-2 md:px-1.5 py-2.5 md:py-1 -ml-2 md:-ml-1.5 rounded-md transition-colors min-h-[44px] md:min-h-0',
                            canUpdate
                                ? 'border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer'
                                : 'cursor-default',
                        ].join(' ')}
                    >
                        {operator ? (
                            <>
                                <AvatarProfile user={operator} size={18} />
                                <span className="font-semibold text-gray-900 dark:text-gray-100 leading-none">
                                    {operator.name} {operator.lastname}
                                </span>
                            </>
                        ) : (
                            <>
                                <HiOutlineUserCircle className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-500 dark:text-gray-400 italic leading-none">
                                    {t('not_assigned', 'Sin asignar')}
                                </span>
                            </>
                        )}
                        {canUpdate && (
                            <HiOutlinePencilAlt
                                className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 opacity-60 group-hover:opacity-100 transition-opacity ml-0.5"
                                aria-hidden="true"
                            />
                        )}
                    </button>
                </Tooltip>

                {parentCategory && (
                    <>
                        <Sep />
                        <span className="inline-flex items-center gap-1.5 leading-none text-gray-700 dark:text-gray-200">
                            <HiOutlineTag className="w-3.5 h-3.5 text-gray-400" />
                            {parentCategory}
                        </span>
                    </>
                )}

                {expirationLabel && (
                    <>
                        <Sep />
                        <span
                            className={[
                                'inline-flex items-center gap-1.5 leading-none',
                                isExpired
                                    ? 'text-rose-600 dark:text-rose-400 font-medium'
                                    : 'text-gray-600 dark:text-gray-300',
                            ].join(' ')}
                        >
                            <HiOutlineClock className="w-3.5 h-3.5" />
                            {isExpired
                                ? `${t('expired', 'Vencido')} ${expirationLabel}`
                                : `${t('expires', 'Vence')} ${expirationLabel}`}
                        </span>
                    </>
                )}
            </div>
        </div>
    )
}

export default TicketMetaStrip
